import { useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import LanguageToggle from './components/LanguageToggle';
import LoginPage from './pages/LoginPage';
import AppShell from './pages/AppShell';
import { initialDrafts, initialRecords } from './data/dashboardData';
import { roleViews, routeForRole, viewRoutes } from './routes';
import './styles/global.css';
import './styles/login.css';
import './styles/layout.css';
import './styles/dashboard.css';
import './styles/workflow.css';
import './styles/responsive.css';
import './styles/language.css';

const SESSION_KEY = 'sfd-authenticated-session';

function readSession() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY));
    return roleViews[saved?.role] ? saved : null;
  } catch {
    return null;
  }
}

function Application() {
  const [session, setSession] = useState(readSession);
  const [records, setRecords] = useState(initialRecords);
  const [drafts, setDrafts] = useState(initialDrafts);
  const location = useLocation();
  const navigate = useNavigate();

  const login = (role) => {
    const nextSession = { role };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
    setSession(nextSession);
    const requestedPath = location.state?.from;
    const requestedView = Object.keys(viewRoutes).find((view) => viewRoutes[view] === requestedPath);
    navigate(requestedView && roleViews[role].includes(requestedView) ? requestedPath : routeForRole(role), { replace: true });
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    navigate('/login', { replace: true });
  };

  const protectedPage = (view) => {
    if (!session) return <Navigate to="/login" replace state={{ from: viewRoutes[view] }} />;
    if (!roleViews[session.role].includes(view)) return <Navigate to={routeForRole(session.role)} replace />;
    return (
      <AppShell
        role={session.role}
        view={view}
        records={records}
        setRecords={setRecords}
        drafts={drafts}
        setDrafts={setDrafts}
        onLogout={logout}
      />
    );
  };

  return (
    <>
      <LanguageToggle />
      <Routes>
        <Route path="/login" element={session ? <Navigate to={routeForRole(session.role)} replace /> : <LoginPage onLogin={login} />} />
        {Object.entries(viewRoutes).map(([view, path]) => <Route key={view} path={path} element={protectedPage(view)} />)}
        <Route path="/" element={<Navigate to={session ? routeForRole(session.role) : '/login'} replace />} />
        <Route path="*" element={<Navigate to={session ? routeForRole(session.role) : '/login'} replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}><Application /></BrowserRouter>;
}
